<template>
  <div class="controller">
    <!-- 操作エリア：プレビュー + ボタン -->
    <div class="panel">
      <div class="preview-stage">
        <div class="timer-preview" :class="[timerFontAdjustClass, { 'preview-has-bg': showBg }]" :style="previewStyle">
          <span v-if="timerSign" class="t-sign">{{ timerSign }}</span>
          <span v-if="timerHours" class="t-digits">{{ timerHours }}</span>
          <span v-if="timerHours" class="t-colon">:</span>
          <span class="t-digits">{{ timerMinutes }}</span>
          <span class="t-colon">:</span>
          <span class="t-digits">{{ timerSeconds }}</span>
        </div>
      </div>
      <div class="controls">
        <button @click="startTimer" class="ctrl-btn ctrl-btn--start">▶ Start</button>
        <button @click="stopTimer" class="ctrl-btn ctrl-btn--stop">■ Stop</button>
        <button @click="resetTimer" class="ctrl-btn ctrl-btn--reset">Reset</button>
      </div>
    </div>

    <!-- タイマー設定 -->
    <div class="panel">
      <h3 class="panel-title">タイマー設定</h3>

      <div class="field">
        <span class="field-label">モード</span>
        <div class="toggle-group">
          <label class="toggle-option">
            <input type="radio" value="up" v-model="mode" />
            <span class="toggle-chip">カウントアップ</span>
          </label>
          <label class="toggle-option">
            <input type="radio" value="down" v-model="mode" />
            <span class="toggle-chip">カウントダウン</span>
          </label>
        </div>
      </div>

      <div class="field">
        <span class="field-label">開始時間</span>
        <div class="time-inputs">
          <label class="time-group">
            <input type="number" v-model.number="inputHours" min="0" max="99" class="time-input" />
            <span class="time-unit">時</span>
          </label>
          <label class="time-group">
            <input type="number" v-model.number="inputMinutes" min="0" max="59" class="time-input" />
            <span class="time-unit">分</span>
          </label>
          <label class="time-group">
            <input type="number" v-model.number="inputSeconds" min="0" max="59" class="time-input" />
            <span class="time-unit">秒</span>
          </label>
        </div>
      </div>

      <div class="field">
        <button @click="startFromMinusFive" class="shortcut-btn">-5秒からカウントアップ</button>
      </div>
    </div>

    <!-- デザイン設定 -->
    <div class="panel">
      <h3 class="panel-title">デザイン</h3>

      <div class="field">
        <span class="field-label">フォント</span>
        <div class="font-grid">
          <label v-for="f in timerFonts" :key="f.value" class="toggle-option">
            <input type="radio" name="fontselect" :value="f.value" v-model="font" />
            <span class="toggle-chip toggle-chip--font" :class="f.cls">09:54</span>
          </label>
        </div>
      </div>

      <div class="field">
        <span class="field-label">カラー</span>
        <div class="preset-list">
          <button
            v-for="(preset, idx) in colorPresets"
            :key="idx"
            class="preset-swatch"
            :class="{ active: selectedPreset === idx }"
            :style="{ backgroundColor: preset.bg, borderColor: preset.border, color: preset.text }"
            @click="selectPreset(idx)"
          >A</button>
        </div>
      </div>

      <div class="field">
        <label class="checkbox-row">
          <input type="checkbox" v-model="showBg" @change="syncBgToStorage" class="checkbox-input" />
          <span class="checkbox-text">背景を表示する</span>
        </label>
      </div>
    </div>

    <!-- 使い方 -->
    <div class="panel">
      <h3 class="panel-title">使い方</h3>
      <ol class="howto-list">
        <li>OBSのブラウザソースを追加します。</li>
        <li>下のボタンでURLをコピーし、URL欄に貼り付けます。</li>
        <li>このコントローラーで操作・デザイン変更すると、ブラウザソースにリアルタイム反映されます。</li>
      </ol>
      <button @click="copyLink" class="copy-btn">{{ copied ? 'コピーしました' : 'ブラウザソース用URLをコピー' }}</button>
    </div>

    <div class="controller-header">
      <img src="../assets/tool-logo.png" alt="timer" class="logo" />
    </div>
  </div>
</template>

<script>
import timerMixin, { TIMER_KEYS, DEFAULT_FONT, fontAdjustClass } from '../mixins/simpleTimer'

export default {
  mixins: [timerMixin],
  data() {
    return {
      timer: null,
      mode: 'up',
      inputHours: 0,
      inputMinutes: 0,
      inputSeconds: 0,
      showBg: true,
      selectedPreset: 0,
      font: DEFAULT_FONT,
      copied: false,
    }
  },
  computed: {
    currentPreset() {
      return this.colorPresets[this.selectedPreset] || this.colorPresets[0];
    },
    timerFontAdjustClass() {
      return fontAdjustClass(this.font);
    },
    previewStyle() {
      const style = {
        fontFamily: this.font,
        color: this.currentPreset.text
      };
      if (this.showBg) {
        style.backgroundColor = this.currentPreset.bg;
        style.border = `0.12em solid ${this.currentPreset.border}`;
        style.borderRadius = '0.12em';
      }
      return style;
    }
  },
  methods: {
    startTimer() {
      this.stopTimer();
      this.totalSeconds = this.inputHours * 3600 + this.inputMinutes * 60 + this.inputSeconds;
      this.runTimer();
    },
    stopTimer() {
      clearInterval(this.timer);
      this.timer = null;
      localStorage.setItem(TIMER_KEYS.running, 'false');
    },
    resetTimer() {
      this.stopTimer();
      this.totalSeconds = 0;
      this.syncSeconds();
    },
    startFromMinusFive() {
      this.stopTimer();
      this.totalSeconds = -5;
      this.mode = 'up';
      this.runTimer();
    },
    runTimer() {
      this.syncSeconds();
      localStorage.setItem(TIMER_KEYS.mode, this.mode);
      localStorage.setItem(TIMER_KEYS.running, 'true');
      this.timer = setInterval(this.tick, 1000);
    },
    tick() {
      if (this.mode === 'up') {
        this.totalSeconds++;
      } else if (this.totalSeconds > 0) {
        this.totalSeconds--;
      } else {
        this.stopTimer();
        return;
      }
      this.syncSeconds();
    },
    syncSeconds() {
      localStorage.setItem(TIMER_KEYS.seconds, this.totalSeconds.toString());
    },
    syncBgToStorage() {
      localStorage.setItem(TIMER_KEYS.showBg, this.showBg ? 'true' : 'false');
    },
    selectPreset(idx) {
      this.selectedPreset = idx;
      localStorage.setItem(TIMER_KEYS.presetIndex, idx.toString());
    },
    copyLink() {
      const url = `${window.location.origin}/tool/simpleTimerDesign`;
      navigator.clipboard.writeText(url).then(() => {
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 2000);
      });
    }
  },
  watch: {
    mode(val) {
      localStorage.setItem(TIMER_KEYS.mode, val);
    },
    font(val) {
      localStorage.setItem(TIMER_KEYS.font, val);
    }
  },
  mounted() {
    const savedSeconds = localStorage.getItem(TIMER_KEYS.seconds);
    if (savedSeconds !== null) {
      this.totalSeconds = parseInt(savedSeconds);
    }
    const savedMode = localStorage.getItem(TIMER_KEYS.mode);
    if (savedMode) {
      this.mode = savedMode;
    }
    this.showBg = localStorage.getItem(TIMER_KEYS.showBg) !== 'false';
    const savedPreset = localStorage.getItem(TIMER_KEYS.presetIndex);
    if (savedPreset !== null) {
      const idx = parseInt(savedPreset);
      if (idx >= 0 && idx < this.colorPresets.length) this.selectedPreset = idx;
    }
    const savedFont = localStorage.getItem(TIMER_KEYS.font);
    if (savedFont) {
      this.font = savedFont;
    }
    // 計測中にドックがリロードされても、保存済みの状態からカウントを再開する
    if (localStorage.getItem(TIMER_KEYS.running) === 'true') {
      this.timer = setInterval(this.tick, 1000);
    }
  },
  beforeUnmount() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Iceland&family=Open+Sans:wght@500&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Josefin+Sans:ital,wght@0,100..700;1,100..700&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Oxanium:wght@200..800&display=swap');

.controller {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 14px;
  background-color: #f8fafc;
  min-height: 100vh;
  font-family: system-ui, -apple-system, sans-serif;
}

.logo {
  height: 120px;
}

/* Panels */
.panel {
  width: 100%;
  max-width: 460px;
  box-sizing: border-box;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
}

.controller-header {
  width: 100%;
  max-width: 460px;
  text-align: center;
}

.panel-title {
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  margin: 0 0 14px 0;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

/* Preview stage */
.preview-stage {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 90px;
  margin-bottom: 14px;
  border-radius: 8px;
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  padding: 12px;
}

.timer-preview {
  font-size: 34px;
  font-weight: bold;
  line-height: 1.2;
  display: inline-flex;
  align-items: center;
  padding: 0.18em 0.6em;
}

.t-digits {
  display: inline-block;
  width: 2.1ch;
  text-align: center;
}

.t-colon {
  display: inline-block;
  width: 0.7ch;
  text-align: center;
  opacity: 0.8;
}

.t-sign {
  display: inline-block;
  width: 0.7ch;
  text-align: center;
}

/* フォントメトリクス補正 */
.timer-preview.font-josefin > span {
  transform: translateY(0.09em);
}

.timer-preview.font-oxanium > span {
  transform: translateY(0.05em);
}

/* Controls */
.controls {
  display: flex;
  gap: 8px;
}

.ctrl-btn {
  flex: 1;
  padding: 12px 8px;
  border-radius: 8px;
  border: none;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.1s;
}

.ctrl-btn:hover {
  opacity: 0.88;
}

.ctrl-btn:active {
  transform: scale(0.97);
}

.ctrl-btn--start {
  background: #22c55e;
  color: #ffffff;
}

.ctrl-btn--stop {
  background: #ef4444;
  color: #ffffff;
}

.ctrl-btn--reset {
  background: #475569;
  color: #ffffff;
}

/* Fields */
.field {
  margin-bottom: 14px;
}

.field:last-child {
  margin-bottom: 0;
}

.field-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 6px;
}

/* Toggle */
.toggle-group {
  display: flex;
  gap: 6px;
}

.toggle-option {
  position: relative;
  cursor: pointer;
}

.toggle-option input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.toggle-chip {
  display: inline-block;
  padding: 6px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #f8fafc;
  color: #64748b;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.15s ease;
}

.toggle-option input:checked + .toggle-chip {
  background: #0f172a;
  color: #ffffff;
  border-color: #0f172a;
}

.toggle-option:hover .toggle-chip {
  border-color: #94a3b8;
}

/* Time Inputs */
.time-inputs {
  display: flex;
  gap: 8px;
}

.time-group {
  display: flex;
  align-items: center;
  gap: 3px;
}

.time-input {
  width: 44px;
  padding: 6px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  text-align: center;
  font-size: 14px;
  background: #f8fafc;
}

.time-input:focus {
  outline: none;
  border-color: #0f172a;
}

.time-unit {
  font-size: 12px;
  color: #64748b;
}

/* Shortcut Button */
.shortcut-btn {
  width: 100%;
  padding: 8px;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  background: transparent;
  color: #475569;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}

.shortcut-btn:hover {
  background: #f1f5f9;
  border-color: #94a3b8;
}

/* Checkbox */
.checkbox-row {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.checkbox-input {
  width: 16px;
  height: 16px;
  accent-color: #0f172a;
}

.checkbox-text {
  font-size: 13px;
  color: #475569;
}

/* Font grid: フォントごとに文字幅が異なるためサイズ固定で揃える */
.font-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
}

.font-grid .toggle-option {
  width: 100%;
}

.toggle-chip--font {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  box-sizing: border-box;
  height: 34px;
  padding: 0 6px;
  font-size: 15px;
  font-variant-numeric: tabular-nums;
}

.font1 { font-family: "Open Sans", sans-serif; }
.font2 { font-family: 'Iceland', cursive; }
.font3 { font-family: 'Josefin Sans', sans-serif; }
.font4 { font-family: 'Oxanium', sans-serif; }

/* Preset */
.preset-list {
  display: flex;
  gap: 8px;
}

.preset-swatch {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border-width: 2.5px;
  border-style: solid;
  cursor: pointer;
  font-weight: bold;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.6;
  transition: all 0.15s ease;
}

.preset-swatch.active {
  opacity: 1;
  transform: scale(1.2);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.preset-swatch:hover {
  opacity: 1;
}

/* How to */
.howto-list {
  margin: 0 0 14px 0;
  padding-left: 18px;
  font-size: 12px;
  color: #475569;
  line-height: 1.7;
}

.copy-btn {
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 8px;
  background: #0f172a;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
}

.copy-btn:hover {
  opacity: 0.88;
}
</style>
